import type {
  BlockedConflict,
  BookingRefundState,
  EventsApproval,
  EventsBookingRefund,
  EventsSession,
} from './api/types';
import { can } from './capabilities';
import { formatMoney } from './proposals';

export const APPROVAL_ACTION_LABEL: Record<string, string> = {
  'proposal.publish': 'Send proposal',
  'payment.refund': 'Refund',
  'confirmation.resolve': 'Blocked confirmation',
};

export const approvalActionLabel = (action: string) =>
  APPROVAL_ACTION_LABEL[action] ??
  action.replace(/[._]/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

/**
 * Why this user can't approve or reject `approval` here, or null when they
 * can. Mirrors events/workflow_commands.py::ResolveApproval so the queue
 * never offers a decision the server will refuse.
 */
export function approvalBlocker(
  approval: EventsApproval,
  session: EventsSession | null | undefined,
  userId: number | null | undefined,
  currency = 'USD',
): string | null {
  if (approval.state !== 'PENDING') return 'Already decided.';
  if (approval.action === 'confirmation.resolve') {
    return 'Resolve this on the booking: retry the same spaces, or cancel and refund.';
  }
  if (!can(session, 'approval.resolve')) {
    return "Your role can't approve requests.";
  }
  if (approval.action === 'payment.refund' && !can(session, 'payment.refund')) {
    return 'Refunds need a director or admin to approve.';
  }
  if (
    userId != null &&
    approval.requested_by != null &&
    approval.requested_by === userId
  ) {
    return 'You requested this, so another approver has to decide.';
  }
  if (approval.amount_cents != null) {
    const limit = session?.approval_limit_cents;
    if (!session?.is_approver || limit == null) {
      return 'Needs an approver with a spending limit.';
    }
    if (limit < approval.amount_cents) {
      return `Above your approval limit of ${formatMoney(limit, currency)}.`;
    }
  }
  return null;
}

/** Oldest first: the queue is worked in order of arrival. */
export function sortPending(rows: EventsApproval[]): EventsApproval[] {
  return [...rows].sort(
    (a, b) => a.created_at.localeCompare(b.created_at) || a.id - b.id,
  );
}

export function blockedConflicts(
  approval: EventsApproval | undefined,
): BlockedConflict[] {
  const conflicts = approval?.payload?.conflicts;
  return Array.isArray(conflicts) ? (conflicts as BlockedConflict[]) : [];
}

/** One sentence per conflict, naming the space when it's known. */
export function conflictText(
  conflict: BlockedConflict,
  spaceName: (id: number) => string | undefined,
): string {
  const space = spaceName(conflict.space_id) ?? 'A space';
  switch (conflict.reason) {
    case 'HOLD':
      return conflict.booking_id
        ? `${space} is held by booking #${conflict.booking_id}.`
        : `${space} is held by another booking.`;
    case 'CAPACITY':
      return `${space} can't fit the signed guest count.`;
    case 'SIGNED_TERMS_MISMATCH':
      return 'The event no longer matches the signed proposal (date, time or spaces changed).';
    case 'SIGNED_TERMS_INVALID':
      return "The signed proposal couldn't be read.";
    default:
      return `${space} isn't available (${conflict.reason.toLowerCase()}).`;
  }
}

export const REFUND_STATE_LABEL: Record<BookingRefundState, string> = {
  REQUESTED: 'Requested',
  SUBMITTED: 'Sent to Stripe',
  SUCCEEDED: 'Refunded',
  FAILED: 'Failed',
};

/** A failed refund can be retried once; the retry is a new row. */
export function canRetryRefund(
  refund: EventsBookingRefund,
  all: EventsBookingRefund[],
): boolean {
  return (
    refund.state === 'FAILED' && !all.some((row) => row.retry_of === refund.id)
  );
}
